/**
 * Hook for fetching progress report data for a specific child.
 * Supports weekly and monthly report types.
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../lib/api";
import type { DisciplineSlug } from "@jumpbean/shared";

// ----- Types -----

export interface DisciplineReportStats {
  slug: DisciplineSlug;
  name: string;
  starsEarned: number;
  challengesCompleted: number;
  timeSpentSeconds: number;
  percentOfTotal: number;
}

export interface BadgeEarned {
  id: string;
  name: string;
  imageUrl: string;
  disciplineName: string;
  disciplineSlug: DisciplineSlug;
  levelNumber: number;
  earnedAt: string;
}

export interface Insight {
  type: "strength" | "improvement" | "celebration" | "tip";
  icon: string;
  message: string;
}

export interface PeriodSummary {
  startDate: string;
  endDate: string;
  totalTimePlayedSeconds: number;
  challengesCompleted: number;
  starsEarned: number;
  badgesEarned: number;
  daysActive: number;
  averageSessionMinutes: number;
}

export interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  streakMaintained: boolean;
}

export interface PeriodComparison {
  hasPreviousPeriod: boolean;
  previousPeriod: PeriodSummary | null;
  changes: {
    timePlayedChange: number;
    challengesChange: number;
    starsChange: number;
  } | null;
}

export interface ProgressReportData {
  childId: string;
  childName: string;
  reportType: "weekly" | "monthly";
  period: {
    start: string;
    end: string;
    label: string;
  };
  summary: PeriodSummary;
  disciplineBreakdown: DisciplineReportStats[];
  badgesEarnedInPeriod: BadgeEarned[];
  streakInfo: StreakInfo;
  insights: Insight[];
  comparison: PeriodComparison;
}

interface ReportApiResponse {
  data: ProgressReportData;
}

export type ReportType = "weekly" | "monthly";

// ----- Hook -----

export function useProgressReport(childId: string | null, reportType: ReportType = "weekly") {
  const [data, setData] = useState<ProgressReportData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    if (!childId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiGet<ProgressReportData>(
        `/api/children/${childId}/reports/${reportType}`
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (!result.data) {
        throw new Error("No report data received");
      }

      setData(result.data);
    } catch (err) {
      console.error("Report fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to load report");
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [childId, reportType]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchReport,
  };
}

// ----- Utility Functions -----

/**
 * Format seconds into a human-readable time string
 */
export function formatPlayTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} seconds`;
  }

  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (hours === 0) {
    return `${mins} minute${mins !== 1 ? "s" : ""}`;
  }

  if (mins === 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }

  return `${hours}h ${mins}m`;
}

/**
 * Format a date for display
 */
export function formatReportDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Calculate a change percentage
 */
export function calculateChangePercent(current: number, previous: number): number {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return Math.round(((current - previous) / previous) * 100);
}

/**
 * Get the color class for a change indicator
 */
export function getChangeColor(change: number): string {
  if (change > 0) return "text-meadow-600";
  if (change < 0) return "text-fairy-600";
  return "text-neutral-500";
}

/**
 * Get the icon for a change indicator
 */
export function getChangeIcon(change: number): string {
  if (change > 0) return "↑";
  if (change < 0) return "↓";
  return "→";
}

export default useProgressReport;
